// React Hooks:
import React, { useState, useEffect } from "react";
// Redux Toolkit Hooks:
import { useAppSelector, useAppDispatch } from "../../../../app/hooks";
// Redux Toolkit Actions & State:
import { editPasswordAction,resetEditPasswordAction, editPassword } from "../editPasswordSlice";
// Formik Framework:
import { useFormik } from "formik";
// Yup for validation:
import * as yup from "yup";
// MUI Components:
import {
  Box,
  SvgIcon,
  Typography,
  Paper,
  useTheme,
  Fade,
  Button,
  TextField,
  InputAdornment,
} from "@mui/material";
import { useSnackbar } from "notistack";
// MUI Styles:
import { makeStyles } from "@mui/styles";
// MUI Icones:
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useNavigate } from "react-router-dom";
import { authentication, logoutAction } from "../../../authentication/authenticationSlice";
import { eraseCookie } from "../../../../configs/cookie-helper/cookieHelper";

const useStyles = makeStyles((theme) => ({
  passwordBox: {
    "&::-webkit-scrollbar": {
      borderRadius: "10px",
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      borderRadius: "10px",
      backdropFilter: "blur(4px)",
      backgroundColor: "rgba(28, 33, 45, 0.4)",
    },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: "10px",
      backgroundColor: theme.palette.secondary.main,
    },
  },
  form: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    height: "100%",
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.secondary,
  },
  field: {
    borderRadius: "5px",
    backgroundColor: "#F2F2F2",
    "& label.Mui-focused": {
      color: theme.palette.text.secondary,
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "#F2F2F2",
    },
    "& .MuiOutlinedInput-root": {
      paddingRight: theme.spacing(0),
      "& fieldset": {
        borderColor: "#F2F2F2",
        borderRadius: "5px",
      },
      "&:hover fieldset": {
        borderColor: "#F2F2F2",
        borderWidth: 1.5,
      },
      "&.Mui-focused fieldset": {
        borderColor: "#F2F2F2",
        borderWidth: 1.5,
      },
    },
  },
}));

const validationSchema = yup.object({
  oldPass: yup
    .string()
    .min(6, "Password must have at least 6 characters.")
    .required("Please enter your old password."),
  password: yup
    .string()
    .min(6, "Password must have at least 6 characters.")
    .required("Please enter your new password."),
  passConf: yup
    .string()
    .oneOf([yup.ref("password")], "Your passwords do not match.")
    .required("Please enter your confirm password."),
});

const EditPasswordForm = () => {
  const { enqueueSnackbar } = useSnackbar();

  const classes = useStyles();
  const theme = useTheme();

  const dispatch = useAppDispatch();

  const { actionStatus, alert } = useAppSelector(editPassword);

  const [showPass, setShowPass] = useState({
    oldPass: false,
    password: false,
    passConf: false,
  });
  const [openButton, setOpenButton] = useState(false);

  const formik = useFormik({
    initialValues: {
      oldPass: "",
      password: "",
      passConf: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      dispatch(editPasswordAction(values));
    },
  });

  useEffect(() => {
    if (
      formik.values.oldPass ||
      formik.values.password ||
      formik.values.passConf
    ) {
      setOpenButton(true);
    } else {
      setOpenButton(false);
    }
  }, [formik.values]);

  useEffect(() => {
    if (actionStatus === "success" && alert.error === false) {
      enqueueSnackbar("Your password has been successfully changed.", {
        variant: "success",
      });
      dispatch(resetEditPasswordAction());
      setOpenButton(false);
      formik.setValues({
        oldPass: "",
        password: "",
        passConf: "",
      });
      formik.setTouched({});
    } else if (actionStatus === "rejected" && alert.error === true) {
      enqueueSnackbar("The old password does not match!", {
        variant: "error",
      });
      dispatch(resetEditPasswordAction());
      formik.setValues(last=>({
        oldPass: "",
        password: last.password,
        passConf: last.passConf,
      }));
    }
  }, [actionStatus, alert.error, dispatch, enqueueSnackbar, formik]);

  const [authStatus, setAuthStatus] = useState({ message: "", loading: false });

  const { token } = useAppSelector(authentication);

  const navigate = useNavigate();

  useEffect(() => {
    if (actionStatus === "loading") {
      setAuthStatus({ message: alert.message, loading: true });
    }

    if (!!token && actionStatus === "success") {
      setAuthStatus({ message: alert.message, loading: false });
    }

    if (actionStatus === "rejected") {
      setAuthStatus({ message: alert.message, loading: false });
    }
  }, [token, actionStatus, alert.message]);

  useEffect(() => {
    if (authStatus.message === "login_again") {
      eraseCookie("token");
      eraseCookie("username");
      eraseCookie("rule");
      navigate("/login");
      dispatch(logoutAction());
    }
  }, [authStatus, navigate, dispatch]);

  return (
    <Paper
      elevation={3}
      sx={{
        width: "100%",
        height: "100%",
        p: "24px 24px 0px 24px",
        borderRadius: "8px",
        overflowY: "auto",
        overflowX: "hidden",
      }}
      className={classes.passwordBox}
    >
      <form onSubmit={formik.handleSubmit} className={classes.form}>
        <Box
          sx={{
            width: "100%",
            height: "fit-content",
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
            mb: "8px",
            [theme.breakpoints.down("lg")]: {
              justifyContent: "center",
            },
          }}
        >
          <Box
            sx={{
              width: "calc(33.33% - 16px)",
              height: "105px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              [theme.breakpoints.down("lg")]: {
                width: "60%",
              },
              [theme.breakpoints.down("md")]: {
                width: "100%",
                height: "98px",
              },
            }}
          >
            <Typography
              sx={{
                width: "100%",
                padding: "8px 8px",
                lineHeight: "1",
                fontSize: "1rem",
                opacity: "0.5",
                [theme.breakpoints.down("md")]: {
                  fontSize: "14px",
                },
              }}
            >
              Old Password
            </Typography>

            <TextField
              name="oldPass"
              type={showPass.oldPass ? "text" : "password"}
              value={formik.values.oldPass}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.oldPass && Boolean(formik.errors.oldPass)}
              size={"small"}
              sx={{
                label: { color: (theme) => theme.palette.text.secondary },
                input: {
                  padding: "8px",
                  color: (theme) => theme.palette.text.secondary,
                  "&::placeholder": {
                    fontSize: "14px",
                    color: (theme) => theme.palette.text.secondary,
                  },
                  "&:-webkit-autofill": {
                    WebkitBoxShadow: "0 0 0 1000px #F2F2F2 inset",
                    WebkitTextFillColor: (theme) =>
                      theme.palette.text.secondary,
                    transition:
                      " background-color 600000s 0s, color 600000s 0s",
                  },
                  [theme.breakpoints.down("md")]: {
                    padding: "6px",
                  },
                },
                width: "100%",
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position="end"
                    sx={{ marginLeft: "14px", marginRight: "14px" }}
                  >
                    <SvgIcon
                      component={
                        showPass.oldPass ? VisibilityOffIcon : VisibilityIcon
                      }
                      inheritViewBox
                      sx={{
                        width: "20px",
                        cursor: "pointer",
                        color: theme.palette.text.secondary,
                        opacity: 0.5,
                      }}
                      onClick={() => {
                        setShowPass((last) => ({
                          ...last,
                          oldPass: !last.oldPass,
                        }));
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              className={classes.field}
            />

            <Typography
              sx={{
                fontSize: "14px",
                color: "#E36464",
                my: "8px",
                [theme.breakpoints.down("md")]: {
                  my: "6px",
                },
                [theme.breakpoints.down("sm")]: {
                  fontSize: "12px",
                },
              }}
            >
              {formik.touched.oldPass &&
                Boolean(formik.errors.oldPass) &&
                formik.errors.oldPass}
            </Typography>
          </Box>

          <Box
            sx={{
              width: "calc(33.33% - 16px)",
              height: "105px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              [theme.breakpoints.down("lg")]: {
                width: "60%",
              },
              [theme.breakpoints.down("md")]: {
                width: "100%",
                height: "98px",
              },
            }}
          >
            <Typography
              sx={{
                width: "100%",
                padding: "8px 8px",
                lineHeight: "1",
                fontSize: "1rem",
                opacity: "0.5",
                [theme.breakpoints.down("md")]: {
                  fontSize: "14px",
                },
              }}
            >
              New Password
            </Typography>

            <TextField
              name="password"
              type={showPass.password ? "text" : "password"}
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              size={"small"}
              sx={{
                label: { color: (theme) => theme.palette.text.secondary },
                input: {
                  padding: "8px",
                  color: (theme) => theme.palette.text.secondary,
                  "&::placeholder": {
                    fontSize: "14px",
                    color: (theme) => theme.palette.text.secondary,
                  },
                  "&:-webkit-autofill": {
                    WebkitBoxShadow: "0 0 0 1000px #F2F2F2 inset",
                    WebkitTextFillColor: (theme) =>
                      theme.palette.text.secondary,
                    transition:
                      " background-color 600000s 0s, color 600000s 0s",
                  },
                  [theme.breakpoints.down("md")]: {
                    padding: "6px",
                  },
                },
                width: "100%",
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position="end"
                    sx={{ marginLeft: "14px", marginRight: "14px" }}
                  >
                    <SvgIcon
                      component={
                        showPass.password ? VisibilityOffIcon : VisibilityIcon
                      }
                      inheritViewBox
                      sx={{
                        width: "20px",
                        cursor: "pointer",
                        color: theme.palette.text.secondary,
                        opacity: 0.5,
                      }}
                      onClick={() => {
                        setShowPass((last) => ({
                          ...last,
                          password: !last.password,
                        }));
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              className={classes.field}
            />

            <Typography
              sx={{
                fontSize: "14px",
                color: "#E36464",
                my: "8px",
                [theme.breakpoints.down("md")]: {
                  my: "6px",
                },
                [theme.breakpoints.down("sm")]: {
                  fontSize: "12px",
                },
              }}
            >
              {formik.touched.password &&
                Boolean(formik.errors.password) &&
                formik.errors.password}
            </Typography>
          </Box>

          <Box
            sx={{
              width: "calc(33.33% - 16px)",
              height: "105px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              [theme.breakpoints.down("lg")]: {
                width: "60%",
              },
              [theme.breakpoints.down("md")]: {
                width: "100%",
                height: "98px",
              },
            }}
          >
            <Typography
              sx={{
                width: "100%",
                padding: "8px 8px",
                lineHeight: "1",
                fontSize: "1rem",
                opacity: "0.5",
                [theme.breakpoints.down("md")]: {
                  fontSize: "14px",
                },
              }}
            >
              Confirm Password
            </Typography>

            <TextField
              name="passConf"
              type={showPass.passConf ? "text" : "password"}
              value={formik.values.passConf}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.passConf && Boolean(formik.errors.passConf)}
              size={"small"}
              sx={{
                label: { color: (theme) => theme.palette.text.secondary },
                input: {
                  padding: "8px",
                  color: (theme) => theme.palette.text.secondary,
                  "&::placeholder": {
                    fontSize: "14px",
                    color: (theme) => theme.palette.text.secondary,
                  },
                  "&:-webkit-autofill": {
                    WebkitBoxShadow: "0 0 0 1000px #F2F2F2 inset",
                    WebkitTextFillColor: (theme) =>
                      theme.palette.text.secondary,
                    transition:
                      " background-color 600000s 0s, color 600000s 0s",
                  },
                  [theme.breakpoints.down("md")]: {
                    padding: "6px",
                  },
                },
                width: "100%",
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position="end"
                    sx={{ marginLeft: "14px", marginRight: "14px" }}
                  >
                    <SvgIcon
                      component={
                        showPass.passConf ? VisibilityOffIcon : VisibilityIcon
                      }
                      inheritViewBox
                      sx={{
                        width: "20px",
                        cursor: "pointer",
                        color: theme.palette.text.secondary,
                        opacity: 0.5,
                      }}
                      onClick={() => {
                        setShowPass((last) => ({
                          ...last,
                          passConf: !last.passConf,
                        }));
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              className={classes.field}
            />

            <Typography
              sx={{
                fontSize: "14px",
                color: "#E36464",
                my: "8px",
                [theme.breakpoints.down("md")]: {
                  my: "6px",
                },
                [theme.breakpoints.down("sm")]: {
                  fontSize: "12px",
                },
              }}
            >
              {formik.touched.passConf &&
                Boolean(formik.errors.passConf) &&
                formik.errors.passConf}
            </Typography>
          </Box>
        </Box>

        {openButton && (
          <Box
            sx={{
              width: "100%",
              height: openButton ? "fit-content" : "0px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mt: "8px",
            }}
          >
            <Fade in={openButton} timeout={500}>
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: "24px",
                }}
              >
                <Button
                  variant="contained"
                  color="error"
                  fullWidth
                  sx={{
                    width: "100px",
                    height: "40px",
                    fontSize: "18px",
                    borderRadius: "8px",
                    textTransform: "none",
                    lineHeight: "0px",
                    padding: "12px",
                    mr: "8px",
                    color: theme.palette.text.secondary,
                    backgroundColor: "transparent",
                    boxShadow: "none",
                    "&:hover": {
                      color: theme.palette.text.primary,
                      transition: "all 0.4s",
                    },
                    [theme.breakpoints.down("md")]: {
                      width: "calc(50% - 4px)",
                    },
                  }}
                  onClick={() => {
                    formik.setValues({
                      oldPass: "",
                      password: "",
                      passConf: "",
                    });
                    formik.setTouched({});
                  }}
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  color="success"
                  fullWidth
                  type="submit"
                  disabled={
                    Object.keys(formik.touched).length > 0 &&
                    Object.keys(formik.errors).length === 0
                      ? false
                      : true
                  }
                  sx={{
                    width: "100px",
                    height: "40px",
                    fontSize: "18px",
                    borderRadius: "8px",
                    textTransform: "none",
                    lineHeight: "0px",
                    padding: "12px",
                    transition: "all 0.4s",
                    ":disabled": {
                      color: theme.palette.success.contrastText,
                      boxShadow: "none",
                      backgroundColor: theme.palette.success.main,
                      opacity: "0.3",
                    },
                    [theme.breakpoints.down("md")]: {
                      width: "calc(50% - 4px)",
                    },
                  }}
                >
                  Save
                </Button>
              </Box>
            </Fade>
          </Box>
        )}
      </form>
    </Paper>
  );
};

export default EditPasswordForm;
